import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Hidden,
  IconButton,
  Paper,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DoubleArrow from '@mui/icons-material/DoubleArrow';
import { Skeleton } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useEffect, useState } from 'react';
import AlertService from '../../shared-services/Notation/AlertService';
import DataInsightService from '../../shared-services/Notation/DataInsightService';
import UpdateNoteService from '../../shared-services/Notation/UpdateNoteService';
import { IAction } from '../../shared-interfaces/Notation/IAction';
import { IMethodOfContact } from '../../shared-interfaces/Notation/IMethodOfContact';
import { INotationComment } from '../../shared-interfaces/Notation/INotationComment';
import {
  GetCoreUserFormattedName,
  ICoreUser,
} from '../../shared-interfaces/User/ICoreUser';
import { NotationType } from '../../shared-types/Notation/NotationType';
import DialogTransition from '../DialogTransition';
import NotationResponseDialog from './NotationResponseDialog';
import { INotation } from '../../shared-interfaces/Notation/INotation';
import NotationService from '../../shared-services/Notation/NotationService';
import DeleteConfirmation from '../DeleteConfirmation';

export interface IExpandedNotation {
  notation: INotation;
  open: boolean;
  onResponseAdded?: (
    action: IAction,
    details: string,
    assignedTo?: ICoreUser,
    methodOfContact?: IMethodOfContact,
  ) => Promise<boolean>;
  close: () => void;
}

const useStyles = makeStyles(theme => ({
  h1: {
    fontSize: '1.6rem',
    fontWeight: 400,
    marginRight: '15px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  titleBar: {
    padding: '10px 12px',
    height: '45px',
    borderBottom: '1px solid #dcdcdc',
  },
  toolbar: {
    position: 'sticky',
    top: '0px',
    zIndex: 10000,
    backgroundColor: 'white',
    height: '48px',
    minHeight: '48px',
  },
  toolbarButton: {
    margin: '0 5px',
  },
  avatar: {
    [theme.breakpoints.down('md')]: {
      height: '50px',
      width: '50px',
    },
    [theme.breakpoints.up('md')]: {
      height: '75px',
      width: '75px',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: '0px',
    color: theme.palette.grey[500],
  },
}));

const CommentLoadingIndicator = () => {
  return (
    <Grid
      container
      style={{
        padding: '8px 4px',
        borderBottom: '1px solid #dcdcdc',
      }}
    >
      <Grid item md={2} lg={2}>
        <Skeleton
          variant="rectangular"
          width={50}
          height={50}
          style={{ float: 'left' }}
        />
      </Grid>
      <Grid item md={9} lg={4}>
        <Grid container direction="column">
          <Skeleton variant="text" width="50%" height="25px" />
          <Skeleton variant="text" width="30%" />
          <Grid item>
            <Skeleton variant="text" width="30%" />
          </Grid>
          <Grid item>
            <Skeleton variant="text" width="30%" />
          </Grid>
        </Grid>
      </Grid>
      <Hidden lgUp>
        <Grid item md={1}>
          <span style={{ float: 'right' }}>
            <Skeleton variant="rectangular" width={25} height={25} />
          </span>
        </Grid>
      </Hidden>
      <Grid item md={12} lg={5}>
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="50%" />
        <Skeleton variant="text" width="25%" />
      </Grid>
      <Hidden lgDown>
        <Grid item lg={1}>
          <span style={{ float: 'right' }}>
            <Skeleton variant="rectangular" width={25} height={25} />
          </span>
        </Grid>
      </Hidden>
    </Grid>
  );
};

const ExpandedNotation = (props: IExpandedNotation) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLessThanSm = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();

  const [responseDialogKey, setResponseDialogKey] = useState(0);
  const [openLowerAlertDialog, setOpenLowerAlertDialog] = useState(false);

  const [comments, setComments] = useState<INotationComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assignedTo, setAssignedTo] = useState<ICoreUser | undefined>(
    props.notation.assignedTo,
  );
  const [additionalAudience, setAdditionalAudience] = useState<
    ICoreUser[] | undefined
  >([]);

  const [focusedComment, setFocusedComment] = useState<
    INotationComment | undefined
  >();
  const [notationResponseDialogOpen, setNotationResponseDialogOpen] = useState(
    false,
  );

  const student = props.notation.student;
  const createdBy = props.notation.createdBy;
  const canHaveComments =
    ['Alert', 'Update', 'DataInsight'].findIndex(
      x => x === props.notation.notationType,
    ) !== -1;

  useEffect(() => {
    var audience = props.notation.advisorConnections
      .filter(x => x.connectionType === 'AdditionalAudienceMember')
      .map(x => x.account);

    setAdditionalAudience(audience);

    if (props.open && props.notation.id && comments.length === 0) {
      if (props.notation.id) {
        NotationService.getComments({
          page: 1,
          pageSize: 500,
          searchParameters: {
            successNotificationId: props.notation.id,
          },
        })
          .then(c => {
            setComments(c.items);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    }
  }, [props.open, props.notation.id]);

  const deleteComment = (x: INotationComment) => (
    <span style={{ float: 'right' }}>
      <IconButton
        onClick={() => setFocusedComment(x)}
        color="secondary"
        aria-label={`Delete response ${x.details}`}
        size="large">
        <DeleteIcon
          style={{
            width: isLessThanSm ? '50px' : undefined,
            height: isLessThanSm ? '50px' : undefined,
          }}
        />
      </IconButton>
    </span>
  );

  return (
    <Dialog
      fullWidth={true}
      fullScreen={isLessThanSm}
      maxWidth={'xl'}
      open={props.open}
      onClose={props.close}
      //@ts-ignore
      TransitionComponent={isLessThanSm ? DialogTransition : undefined}
    >
      <DialogTitle className={classes.titleBar}>
        <Typography variant="h1" className={classes.h1}>
          {props.notation.notationType} Details
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={props.close}
          size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          <Grid
            item
            container
            spacing={1}
            style={
              props.notation.id
                ? { padding: '8px 4px', borderBottom: '3px solid #dcdcdc' }
                : { padding: '8px 4px' }
            }
          >
            <Grid item xs={2} md={2} lg={2}>
              {student && (
                <Avatar
                  variant="rounded"
                  className={classes.avatar}
                  alt={GetCoreUserFormattedName(student, false)}
                  src={
                    student.profileImageFilePath ??
                    student.institutionProfileImageFilePath
                  }
                />
              )}
            </Grid>
            <Grid item xs={10} md={10} lg={5}>
              <Typography variant="h2">
                {props.notation.notationType} for{' '}
                <a
                  href={`/beacon/students/advise/student/${student?.externalId}`}
                  target="_blank"
                >
                  {GetCoreUserFormattedName(student, false)}
                </a>
              </Typography>
              <Typography variant="caption">
                {createdBy &&
                  `by ${GetCoreUserFormattedName(createdBy, false)} - `}
                {props.notation.createdOn.toLocaleDateString()} at{' '}
                {props.notation.createdOn.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              {props.notation.audienceType && (
                <Typography component="div">
                  <strong>Audience: </strong>
                  {props.notation.audienceType}
                </Typography>
              )}
              {additionalAudience && additionalAudience.length > 0 && (
                <Typography component="div">
                  <strong>Additional Audience: </strong>
                  {additionalAudience.map(x => {
                    if (
                      additionalAudience[additionalAudience.length - 1].id ===
                      x.id
                    ) {
                      return GetCoreUserFormattedName(x, false);
                    } else {
                      return `${GetCoreUserFormattedName(x, false)}, `;
                    }
                  })}
                </Typography>
              )}
              {props.notation.categories &&
                props.notation.categories.length > 0 && (
                  <Typography component="div">
                    <strong>Categories: </strong>
                    {props.notation.categories.map(x => x.name).join(', ')}
                  </Typography>
                )}
              {assignedTo && (
                <Typography component="div">
                  <strong>Assigned To: </strong>
                  {GetCoreUserFormattedName(assignedTo, false)}
                </Typography>
              )}
            </Grid>
            <Grid item md={12} lg={5}>
              <Typography
                component="div"
                dangerouslySetInnerHTML={{ __html: props.notation.details }}
              />
            </Grid>
          </Grid>
          <Paper
            variant="outlined"
            style={
              canHaveComments && !isLessThanSm
                ? {
                    height: '500px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    marginTop: '10px',
                  }
                : { marginTop: '10px' }
            }
          >
            {canHaveComments && (
              <Toolbar className={classes.toolbar} component={Paper} square>
                <Box display="flex">
                  <Button
                    variant="outlined"
                    onClick={() => setNotationResponseDialogOpen(true)}
                    className={classes.toolbarButton}
                  >
                    <AddIcon />
                    Add Response
                  </Button>
                  {props.notation.notationType === NotationType.Alert &&
                    props.notation.alertStatus === 'Open' && (
                      <Button
                        variant="outlined"
                        onClick={() => setOpenLowerAlertDialog(true)}
                        className={classes.toolbarButton}
                      >
                        <DoubleArrow style={{ transform: 'rotate(90deg)' }} />
                        Lower Alert
                      </Button>
                    )}
                </Box>
              </Toolbar>
            )}
            <Grid
              item
              container
              direction="column"
              style={{
                paddingLeft: '25px',
              }}
            >
              {isLoading && canHaveComments ? (
                [...Array(50)].map((e, i) => (
                  <CommentLoadingIndicator key={i} />
                ))
              ) : comments.length > 0 ? (
                comments.map((x: INotationComment, i) => (
                  <Grid
                    container
                    key={x.id}
                    spacing={2}
                    style={{
                      padding: '12px 4px',
                      borderBottom: '1px solid #dcdcdc',
                    }}
                  >
                    <Grid item xs={2} md={2} lg={2}>
                      <Avatar
                        variant="rounded"
                        style={{
                          height: '50px',
                          width: '50px',
                          float: 'left',
                        }}
                        alt={`${x.createdBy.firstName} ${x.createdBy.lastName}`}
                        src={
                          x.createdBy.profileImageFilePath ??
                          x.createdBy.institutionProfileImageFilePath
                        }
                      />
                    </Grid>
                    <Grid item xs={10} md={10} lg={4}>
                      <Grid container direction="column">
                        {x.createdBy && (
                          <Grid item>
                            <Typography>
                              {x.createdBy.preferredFirstName ??
                                x.createdBy.firstName}{' '}
                              {x.createdBy.lastName}
                            </Typography>
                            <Typography variant="caption">
                              {x.createdOn.toLocaleDateString()} at{' '}
                              {x.createdOn.toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Typography>
                          </Grid>
                        )}
                        {x.action && (
                          <Grid item>
                            <Typography>
                              <strong>Action: </strong>
                              {x.action.name}
                            </Typography>
                          </Grid>
                        )}
                        {x.methodOfContact && (
                          <Grid item>
                            <Typography>
                              <strong>Method of Contact: </strong>{' '}
                              {x.methodOfContact.name}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={12} lg={5}>
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{ __html: x.details }}
                      />
                    </Grid>
                    <Hidden only={['xs', 'sm']}>
                      <Grid item lg={1}>
                        {deleteComment(x)}
                      </Grid>
                    </Hidden>
                  </Grid>
                ))
              ) : canHaveComments ? (
                <Box
                  height="500px"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h2">
                    There are currently no responses.
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Grid>
          </Paper>
        </Grid>
      </DialogContent>
      <NotationResponseDialog
        // key={responseDialogKey}
        open={notationResponseDialogOpen}
        close={() => setNotationResponseDialogOpen(false)}
        notationType={props.notation.notationType}
        assignable={props.notation.notationType === 'Alert'}
        additionalAudience={additionalAudience}
        transition={isLessThanSm}
        audience={props.notation.audienceType}
        title={`Add Response`}
        assignedTo={props.notation.assignedTo}
        for={props.notation.student}
        handleResponse={(
          action: IAction,
          details: string,
          assignedTo?: ICoreUser,
          methodOfContact?: IMethodOfContact,
          additionalAudience?: ICoreUser[],
        ) => {
          return (async () => {
            let comments = [] as INotationComment[];
            try {
              if (props.notation.notationType === 'Alert') {
                await AlertService.addComment(
                  props.notation.id,
                  action,
                  details,
                  assignedTo,
                  additionalAudience,
                  methodOfContact,
                );
                comments = await AlertService.getComments(props.notation.id);
              } else if (props.notation.notationType === 'Update') {
                await UpdateNoteService.addComment(
                  props.notation.id,
                  action,
                  details,
                  assignedTo,
                  methodOfContact,
                );
                comments = await UpdateNoteService.getComments(
                  props.notation.id,
                );
              } else if (props.notation.notationType === 'DataInsight') {
                await DataInsightService.addComment(
                  props.notation.id,
                  action,
                  details,
                  assignedTo,
                  methodOfContact,
                );
                comments = await DataInsightService.getComments(
                  props.notation.id,
                );
              }

              if (props.onResponseAdded) {
                await props.onResponseAdded(
                  action,
                  details,
                  assignedTo,
                  methodOfContact,
                );
              }
              setAssignedTo(assignedTo);
              setAdditionalAudience(additionalAudience);

              comments.sort(
                (a, b) => b.createdOn.getTime() - a.createdOn.getTime(),
              );
              setComments(comments);
              enqueueSnackbar('Added Response', { variant: 'success' });
              setNotationResponseDialogOpen(false);
              setResponseDialogKey(responseDialogKey + 1);
              return true;
            } catch (e) {
              enqueueSnackbar('Failed to Add Response', { variant: 'error' });
              setNotationResponseDialogOpen(false);
              return false;
            }
          })();
        }}
      />
      <NotationResponseDialog
        // key={}
        open={openLowerAlertDialog}
        notationType={props.notation.notationType}
        close={() => setOpenLowerAlertDialog(false)}
        assignable={false}
        transition={isLessThanSm}
        title={`Lower Alert for ${GetCoreUserFormattedName(
          props.notation.student,
          false,
        )}`}
        audience={props.notation.audienceType}
        assignedTo={props.notation.assignedTo}
        for={props.notation.student}
        handleResponse={(
          action: IAction,
          details: string,
          assignedTo?: ICoreUser,
          methodOfContact?: IMethodOfContact,
        ) => {
          return (async () => {
            try {
              const success = await AlertService.lower(
                props.notation.id,
                action.id,
                details,
                methodOfContact?.id,
                assignedTo?.id,
              );
              if (success) {
                AlertService.getComments(props.notation.id)
                  .then(x => {
                    setComments(
                      x.sort(
                        (a, b) => b.createdOn.getTime() - a.createdOn.getTime(),
                      ),
                    );
                  })
                  .then(() => {
                    enqueueSnackbar('Lowered Alert', { variant: 'success' });
                  });
                return true;
              } else {
                enqueueSnackbar('Failed to Lower Alert', { variant: 'error' });
                return false;
              }
            } catch {
              enqueueSnackbar('Failed to Lower Alert', { variant: 'error' });
              return false;
            }
          })();
        }}
      />
      <DeleteConfirmation
        title="Are you sure you want to delete this response?"
        close={() => setFocusedComment(undefined)}
        open={Boolean(focusedComment)}
        onDelete={() => {
          (async () => {
            try {
              const notationId = focusedComment?.successNotificationId;
              const commentId = focusedComment?.id;
              if (notationId && commentId) {
                switch (props.notation.notationType) {
                  case 'Alert':
                    await AlertService.deleteComment(notationId, commentId);
                    break;
                  case 'Update':
                    await UpdateNoteService.deleteComment(
                      notationId,
                      commentId,
                    );
                    break;
                  case 'DataInsight':
                    await DataInsightService.deleteComment(
                      notationId,
                      commentId,
                    );
                    break;
                }
                enqueueSnackbar('Deleted Response', {
                  variant: 'success',
                });
                setComments(comments.filter(x => x.id !== commentId));
              }
            } catch {
              enqueueSnackbar('Response Deletion Failed', {
                variant: 'error',
              });
            }
            setFocusedComment(undefined);
          })();
        }}
      />
    </Dialog>
  );
};

export default ExpandedNotation;
